import React, { useState } from 'react';
import styled from 'styled-components';
import { MdClose } from 'react-icons/md';
import OverlayControlEditor from './OverlayControlEditor';
import OverlayPreviewFrame from './OverlayPreviewFrame';
import PromotionOverlay from "./PromotionOverlay";
import MainButton from '../../../../../shared/layout/buttons/MainButton';
import {
    StyledInput,
    StyledLabel,
    FormGroup
} from '../../../../../shared/layout/forms/FormElements';

const EditPromotionOverlay = ({ promotion, onSave, onCancel }) => {
    const [overlay, setOverlay] = useState(() => new PromotionOverlay(promotion.promotionOverlay));
    const [activeTab, setActiveTab] = useState('edit');
    const [isPlaying, setIsPlaying] = useState(false);
    const [, forceUpdate] = useState(0);

    const handleInputChange = (field, value) => {
        setOverlay((prev) => {
            prev[field] = value;
            return prev;
        });
        forceUpdate((prev) => prev + 1);
    };

    const handleFieldSettingChange = (index, field, value) => {
        setOverlay((prev) => {
            const updatedFieldSettings = [...prev.fieldSettings];
            updatedFieldSettings[index][field] = value;
            prev.fieldSettings = updatedFieldSettings;
            return prev;
        });
        forceUpdate((prev) => prev + 1);
    };

    const handleSave = () => onSave(overlay);

    return (
        <Backdrop>
            <ModalShell>
                <ModalHeader>
                    <HeaderTitle>Edit Promotion Overlay</HeaderTitle>
                    <CloseIconButton onClick={onCancel}>
                        <MdClose size={24} />
                    </CloseIconButton>
                </ModalHeader>
                <ModalBody>
                    <TabsContainer>
                        <Tabs>
                            <Tab active={activeTab === 'edit'} onClick={() => setActiveTab('edit')}>
                                Editor
                            </Tab>
                            <Tab active={activeTab === 'preview'} onClick={() => setActiveTab('preview')}>
                                Preview
                            </Tab>
                        </Tabs>
                    </TabsContainer>

                    {activeTab === 'edit' && (
                        <Content>
                            <PreviewPane>
                                <PreviewToolbar>
                                    <div>
                                        <h5 className="mb-1" style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                                            {overlay.name || 'Untitled overlay'}
                                        </h5>
                                        <small style={{ color: '#6b7280' }}>
                                            Background: {overlay.backgroundImage ? 'Custom' : 'None'}
                                        </small>
                                    </div>
                                    <MainButton size="small" variant="secondary" onClick={() => setIsPlaying((prev) => !prev)}>
                                        {isPlaying ? 'Stop animation' : 'Play animation'}
                                    </MainButton>
                                </PreviewToolbar>
                                <OverlayPreviewFrame
                                    overlay={overlay}
                                    promotion={promotion}
                                    scale={0.85}
                                    animated={isPlaying}
                                />
                            </PreviewPane>

                            <InspectorPane>
                                <GlobalSettingsCard>
                                    <FormGroup $noMargin>
                                        <StyledLabel>Overlay Name</StyledLabel>
                                        <StyledInput
                                            type="text"
                                            value={overlay.name}
                                            onChange={(e) => handleInputChange('name', e.target.value)}
                                            placeholder="e.g., Summer banner"
                                        />
                                    </FormGroup>
                                    <FormGroup $noMargin>
                                        <StyledLabel>Background Image URL</StyledLabel>
                                        <StyledInput
                                            type="text"
                                            value={overlay.backgroundImage}
                                            onChange={(e) =>
                                                handleInputChange('backgroundImage', e.target.value)
                                            }
                                            placeholder="https://example.com/background.png"
                                        />
                                    </FormGroup>
                                </GlobalSettingsCard>
                                <Separator />
                                <FieldSettingsScroll>
                                    {overlay.fieldSettings.map((fieldSetting, index) => (
                                        <OverlayControlEditor
                                            key={`${fieldSetting.overlay_field_id}-${index}`}
                                            fieldSetting={fieldSetting}
                                            index={index}
                                            onChange={handleFieldSettingChange}
                                        />
                                    ))}
                                </FieldSettingsScroll>
                            </InspectorPane>
                        </Content>
                    )}

                    {activeTab === 'preview' && (
                        <Content>
                            <PreviewPane style={{ flex: 1 }}>
                                <PreviewToolbar>
                                    <h5 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                                        {overlay.name || 'Overlay preview'}
                                    </h5>
                                    <MainButton size="small" variant="secondary" onClick={() => setIsPlaying((prev) => !prev)}>
                                        {isPlaying ? 'Stop animation' : 'Play animation'}
                                    </MainButton>
                                </PreviewToolbar>
                                <OverlayPreviewFrame
                                    overlay={overlay}
                                    promotion={promotion}
                                    scale={1}
                                    animated={isPlaying}
                                />
                            </PreviewPane>
                        </Content>
                    )}
                </ModalBody>
                <ModalFooter>
                    <MainButton variant="danger" onClick={onCancel}>
                        Cancel
                    </MainButton>
                    <MainButton onClick={handleSave}>
                        Save Overlay
                    </MainButton>
                </ModalFooter>
            </ModalShell>
        </Backdrop>
    );
};

export default EditPromotionOverlay;

const Backdrop = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1050;
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(4px);
`;

const ModalShell = styled.div`
    width: clamp(900px, 90vw, 1280px);
    height: clamp(600px, 92vh, 960px);
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const ModalHeader = styled.div`
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #ffffff;
`;

const HeaderTitle = styled.h4`
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
`;

const CloseIconButton = styled.button`
    background: transparent;
    border: none;
    color: #6b7280;
    cursor: pointer;
    padding: 0.25rem;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    &:hover {
        background: #f3f4f6;
        color: #111827;
    }
`;

const ModalBody = styled.div`
    flex: 1;
    padding: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: #f9fafb;
`;

const TabsContainer = styled.div`
    padding: 1rem 1.5rem;
    background: #ffffff;
    border-bottom: 1px solid #e5e7eb;
`;

const ModalFooter = styled.div`
    padding: 1.25rem 1.5rem;
    border-top: 1px solid #e5e7eb;
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    background: #ffffff;
`;

const Tabs = styled.div`
    display: inline-flex;
    background: #f3f4f6;
    border-radius: 8px;
    padding: 0.25rem;
`;

const Tab = styled.button`
    border: none;
    border-radius: 6px;
    padding: 0.5rem 1rem;
    font-weight: 500;
    font-size: 0.875rem;
    color: ${({ active }) => (active ? '#111827' : '#6b7280')};
    background: ${({ active }) => (active ? '#ffffff' : 'transparent')};
    box-shadow: ${({ active }) => (active ? '0 1px 2px rgba(0,0,0,0.05)' : 'none')};
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
        color: #111827;
    }
`;

const Content = styled.div`
    flex: 1;
    display: flex;
    gap: 1.5rem;
    padding: 1.5rem;
    overflow: hidden;
`;

const PreviewPane = styled.div`
    flex: 1.5;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    min-width: 0;
`;

const InspectorPane = styled.div`
    flex: 1;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    min-width: 0;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const GlobalSettingsCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const FieldSettingsScroll = styled.div`
    flex: 1;
    overflow-y: auto;
    padding-right: 0.25rem;
    margin-right: -0.625rem;
    padding-right: 0.625rem; /* offset scrollbar */
    
    &::-webkit-scrollbar {
        width: 6px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background-color: #d1d5db;
        border-radius: 20px;
    }
`;

const PreviewToolbar = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.5rem;
`;

const Separator = styled.div`
    height: 1px;
    background: #e5e7eb;
    width: 100%;
`;
